import api from './api';
import {
  Calendar,
  CreateCalendarData,
  UpdateCalendarData,
  CalendarFilters,
  CalendarPostsParams,
  CalendarApiResponse,
  CalendarEvent,
} from '@/types/calendar';
import { Post } from './posts';

/**
 * Calendar API Service
 *
 * API client for calendar management endpoints
 */

export const calendarService = {
  /**
   * List all calendars with optional filters
   */
  getCalendars: async (params?: CalendarFilters): Promise<Calendar[]> => {
    const queryParams = new URLSearchParams();

    if (params?.isDefault !== undefined) {
      queryParams.append('isDefault', params.isDefault.toString());
    }
    if (params?.isActive !== undefined) {
      queryParams.append('isActive', params.isActive.toString());
    }

    const { data } = await api.get<CalendarApiResponse<Calendar[]>>(
      `/api/calendars${queryParams.toString() ? `?${queryParams.toString()}` : ''}`
    );
    return data.data;
  },

  /**
   * Get single calendar by ID
   */
  getCalendar: async (id: number): Promise<Calendar> => {
    const { data } = await api.get<CalendarApiResponse<Calendar>>(`/api/calendars/${id}`);
    return data.data;
  },

  /**
   * Get default calendar (auto-creates if doesn't exist)
   */
  getDefaultCalendar: async (): Promise<Calendar> => {
    const { data } = await api.get<CalendarApiResponse<Calendar>>('/api/calendars/default');
    return data.data;
  },

  /**
   * Create new calendar
   */
  createCalendar: async (calendarData: CreateCalendarData): Promise<Calendar> => {
    const { data } = await api.post<CalendarApiResponse<Calendar>>(
      '/api/calendars',
      calendarData
    );
    return data.data;
  },

  /**
   * Update calendar
   */
  updateCalendar: async (id: number, calendarData: UpdateCalendarData): Promise<Calendar> => {
    const { data } = await api.patch<CalendarApiResponse<Calendar>>(
      `/api/calendars/${id}`,
      calendarData
    );
    return data.data;
  },

  /**
   * Delete calendar (soft delete)
   */
  deleteCalendar: async (id: number): Promise<void> => {
    await api.delete(`/api/calendars/${id}`);
  },

  /**
   * Get posts for a specific calendar
   */
  getCalendarPosts: async (id: number, params: CalendarPostsParams): Promise<Post[]> => {
    const queryParams = new URLSearchParams({
      startDate: params.startDate,
      endDate: params.endDate,
    });

    if (params.platform) {
      queryParams.append('platform', params.platform);
    }
    if (params.status) {
      queryParams.append('status', params.status);
    }
    if (params.timezone) {
      queryParams.append('timezone', params.timezone);
    }

    // Social calendar entry — a scheduled/mapped Post occurrence (unchanged shape)
    interface SocialCalendarEntry {
      id?: number; // CalendarPost ID (for scheduled)
      mappingId?: number; // CalendarMapping ID (for mapped)
      scheduledTime: string;
      status: string;
      source: 'scheduled' | 'mapped';
      variationId?: number;
      variationTitle?: string | null; // Variation title for display
      post: Post;
    }

    // Blog calendar entry — a read-only WordPress blog post occurrence.
    // Only returned for "blog calendars" (Calendar.channelId set); never mixed
    // with SocialCalendarEntry in the same calendar.
    interface BlogCalendarEntry {
      source: 'blog';
      blogPostId: number;
      scheduledTime: string;
      status: 'SCHEDULED' | 'PUBLISHED' | 'FAILED';
      blog: {
        id: number;
        title: string;
        status: 'SCHEDULED' | 'PUBLISHED' | 'FAILED';
        wpPostUrl: string | null;
        featuredImageUrl: string | null;
        siteIconUrl: string | null;
        siteHost: string;
        lastError?: string | null;
      };
    }

    type CalendarEntry = SocialCalendarEntry | BlogCalendarEntry;

    interface CalendarPostsResponse {
      dateRange: { startDate: string; endDate: string };
      postsByDate: Record<string, CalendarEntry[]>;
      totalPosts: number;
    }

    const { data } = await api.get<CalendarApiResponse<CalendarPostsResponse>>(
      `/api/calendars/${id}/posts?${queryParams.toString()}`
    );

    // Extract posts from postsByDate structure and flatten into array
    const postsArray: Post[] = [];
    if (data.data?.postsByDate) {
      Object.values(data.data.postsByDate).forEach((calendarEntries) => {
        calendarEntries.forEach((entry) => {
          if (entry.source === 'blog') {
            // Adapt the blog entry into a synthetic READ-ONLY Post so it can
            // render through the same calendar chip/popover/day-panel components
            // as social posts. There is no `entry.post` to spread here.
            postsArray.push({
              id: entry.blogPostId,
              caption: entry.blog.title,
              status: entry.blog.status,
              scheduledAt: entry.scheduledTime,
              publishedAt: entry.blog.status === 'PUBLISHED' ? entry.scheduledTime : null,
              postChannels: [
                {
                  id: entry.blogPostId,
                  channelId: entry.blogPostId,
                  channel: {
                    id: entry.blogPostId,
                    platform: 'WORDPRESS',
                    platformUsername: entry.blog.siteHost,
                    isActive: true,
                    profilePhotoUrl: entry.blog.siteIconUrl || undefined,
                  },
                  externalUrl:
                    (entry.blog.status === 'PUBLISHED' || entry.blog.status === 'SCHEDULED') &&
                    entry.blog.wpPostUrl
                      ? entry.blog.wpPostUrl
                      : undefined,
                  status:
                    entry.blog.status === 'PUBLISHED'
                      ? 'PUBLISHED'
                      : entry.blog.status === 'SCHEDULED'
                        ? 'SCHEDULED'
                        : entry.blog.status === 'FAILED'
                          ? 'FAILED'
                          : undefined,
                  error: entry.blog.status === 'FAILED' ? (entry.blog.lastError ?? undefined) : undefined,
                },
              ],
              postMedia: entry.blog.featuredImageUrl
                ? [
                    {
                      id: entry.blogPostId,
                      type: 'IMAGE',
                      originalUrl: entry.blog.featuredImageUrl,
                      thumbnailUrl: entry.blog.featuredImageUrl,
                      order: 0,
                    },
                  ]
                : [],
              aiGenerated: false,
              labels: [],
              createdAt: entry.scheduledTime,
              updatedAt: entry.scheduledTime,
              source: 'blog',
              blogPostId: entry.blogPostId,
              blogStatus: entry.blog.status,
              wpPostUrl: entry.blog.wpPostUrl,
              siteIconUrl: entry.blog.siteIconUrl,
              siteHost: entry.blog.siteHost,
            });
            return;
          }

          // Merge scheduledTime, status, source, and mapping info to Post
          postsArray.push({
            ...entry.post,
            scheduledAt: entry.scheduledTime,
            status: entry.status as Post['status'],
            source: entry.source,
            mappingId: entry.mappingId,
            variationId: entry.variationId,
            variationTitle: entry.variationTitle,
          });
        });
      });
    }

    return postsArray;
  },

  /**
   * Get calendar events (notes + holidays) for date range
   */
  getCalendarEvents: async (
    calendarId: number,
    from: string,
    to: string
  ): Promise<CalendarEvent[]> => {
    const queryParams = new URLSearchParams({
      from,
      to,
    });

    const { data } = await api.get<CalendarApiResponse<CalendarEvent[]>>(
      `/api/calendars/${calendarId}/events?${queryParams.toString()}`
    );
    return data.data;
  },
};
